import { useCallback, useEffect, useRef } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Google Preferred Sources button.
 *
 * Google does NOT expose a plain URL for "add as preferred source" — the old
 * `https://www.google.com/preferred_sources?...` link 404s because that path
 * doesn't exist. The only supported integration is an embed: load Google's
 * publisher.js library and place a `<div google-add-preferred-source-btn>`,
 * which Google renders into an interactive, localized button that marks this
 * site as the reader's preferred source and returns them to the page.
 *
 * Docs: https://developers.google.com/search/docs/appearance/preferred-sources
 */

const PUBLISHER_SRC = "https://news.google.com/swg/js/v1/publisher.js";

interface PreferredSourceClient {
  init: (options: { theme: "light" | "dark"; lang: string }) => void;
  addPreferredSource: () => void;
}

type PreferredSourceCallback = (client: PreferredSourceClient) => void;

declare global {
  interface Window {
    PREFERRED_SOURCE?: PreferredSourceCallback[];
  }
}

let scriptPromise: Promise<void> | null = null;

function loadPublisherScript(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${PUBLISHER_SRC}"]`,
    ) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("publisher.js failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = PUBLISHER_SRC;
    s.async = true;
    s.setAttribute("preferred-sources-control", "manual");
    s.addEventListener("load", () => {
      s.dataset.loaded = "true";
      resolve();
    });
    s.addEventListener("error", () => reject(new Error("publisher.js failed")));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

interface PreferredSourceButtonProps {
  /** "light" for dark backgrounds (e.g. footer), "default" for white content areas */
  variant?: "default" | "light";
  className?: string;
}

export const PreferredSourceButton = ({
  variant = "default",
  className = "",
}: PreferredSourceButtonProps) => {
  const clientRef = useRef<PreferredSourceClient | null>(null);
  const theme = variant === "light" ? "dark" : "light";

  useEffect(() => {
    let mounted = true;
    window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || [];
    window.PREFERRED_SOURCE.push((client) => {
      if (!mounted) return;
      client.init({ theme, lang: "en" });
      clientRef.current = client;
    });
    void loadPublisherScript().catch(() => {
      /* The visible button remains available for a later retry. */
    });

    return () => {
      mounted = false;
      clientRef.current = null;
    };
  }, [theme]);

  const handleClick = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.addPreferredSource();
      return;
    }

    // If the user clicks before Google's async library is ready, run the flow
    // as soon as its callback queue is processed.
    window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || [];
    window.PREFERRED_SOURCE.push((client) => {
      client.init({ theme, lang: "en" });
      clientRef.current = client;
      client.addPreferredSource();
    });
    scriptPromise = null;
    void loadPublisherScript();
  }, [theme]);

  return (
    <Button
      type="button"
      onClick={handleClick}
      className={`gap-2 whitespace-normal text-left ${
        variant === "light"
          ? "border border-background/20 bg-background/10 text-background hover:bg-background/20"
          : "bg-primary text-primary-foreground hover:bg-primary/90"
      } ${className}`}
      aria-label="Add Garden-Review as a preferred source on Google"
    >
      <Star className="h-4 w-4 shrink-0" aria-hidden="true" />
      Add Garden-Review to Google Preferred Sources
    </Button>
  );
};

export default PreferredSourceButton;
